import { computed, Directive, effect, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, Observable } from 'rxjs';

import { ColorConstants, EmptyPage, IconConfiguration } from '@celum/common-components';
import { Specification } from '@celum/formats/domain';
import {
  FormatFormServiceLike,
  FormatServiceLike,
  FormatStoreLike,
  FormatViewLike,
  NotificationService,
  resolveAssetPath,
  SpecificationStore
} from '@celum/formats/shared';
import { AlwaysShowErrorMatcher } from '@celum/ng2base';

import { ChangeWarningComponent, ChangeWarningResult } from './change-warning/change-warning.component';

@Directive()
export abstract class AbstractFormatViewDialog<TFormat extends FormatViewLike, TCreate, TUpdate> implements OnInit, OnDestroy {
  protected data = inject(MAT_DIALOG_DATA);

  protected readonly icons = {
    close: IconConfiguration.small('standard-cancel').withColor(ColorConstants.BLUE_GRAY_900),
    create: IconConfiguration.medium('check-m').withColor('inherit'),
    info: IconConfiguration.small('info-icon').withColor(ColorConstants.BLUE_GRAY_900).withIconSize(12),
    arrow: IconConfiguration.medium('arrow-down-xs').withColor(ColorConstants.BLUE_GRAY_900).withIconSize(12),
    protected: IconConfiguration.small(resolveAssetPath('format-protected')),
    autoImportExcluded: IconConfiguration.small('import')
  };

  protected readonly alwaysShowMatcher = new AlwaysShowErrorMatcher();

  protected readonly validationLimits = {
    key: 55,
    name: 55,
    description: 255
  };

  protected readonly noValidSpecConfig = EmptyPage.noActionConfig(
    'no-valid-specification',
    resolveAssetPath('specification-removed'),
    'FORMAT_VIEW_DIALOG.ERROR.INVALID_SPEC',
    'normal',
    232,
    'unset'
  );

  protected invalidSpecValidator: ValidatorFn = () => {
    const invalidSpecDueToUnassign =
      this.formatDegraded?.() && this.formatSelectedSpecId?.() && this.formatSelectedSpecId?.() === this.specStore?.selectedSpecId();
    const invalidSpecDueToSpecRemoval = this.formatDegraded?.() && !this.formatSelectedSpecId?.() && !this.specStore?.selectedSpecId();
    return invalidSpecDueToUnassign || invalidSpecDueToSpecRemoval ? { invalidSpec: true } : null;
  };
  protected specControl = new FormControl<number>(null, [this.invalidSpecValidator]);

  protected specStore = inject(SpecificationStore);
  protected notificationService = inject(NotificationService);

  protected formatDegraded = computed(() => this.formatStore.selectedFormat()?.status?.state === 'DEGRADED');
  protected formatSelectedSpecId = computed(() => this.formatStore.selectedFormat()?.specificationReference?.id);
  // Format is degraded AND ((Specification is removed from format) OR (Specification on the format is the same one which is being selected))
  protected invalidSpecSelected = computed(
    () =>
      this.formatDegraded() &&
      ((!this.formatSelectedSpecId() && !this.specStore.selectedSpecId()) ||
        (this.formatSelectedSpecId() && this.specStore.selectedSpecId() === this.formatSelectedSpecId()))
  );

  protected showHiddenFields: boolean;

  private matDialog = inject(MatDialog);

  protected abstract readonly formatFormService: FormatFormServiceLike<TCreate>;
  protected abstract readonly formatService: FormatServiceLike<TFormat, TCreate, TUpdate>;
  protected abstract readonly formatStore: FormatStoreLike<TFormat>;
  protected abstract readonly isAdmin: Signal<boolean>;
  protected abstract readonly showAutoImportExcludedToggle: boolean;
  protected abstract readonly showHiddenFieldsStorageKey: string;
  protected abstract readonly dialogRef: MatDialogRef<unknown>;
  protected abstract readonly logPrefix: string;

  constructor() {
    effect(() => {
      this.specControl.patchValue(this.specStore.selectedSpecId() ?? 0, { emitEvent: false });
      this.specControl.updateValueAndValidity({ emitEvent: false });
    });

    this.specControl.valueChanges.subscribe(value => this.specStore.updateSelectedSpecId(value));

    effect(() => {
      if (this.isAdmin()) {
        this.showHiddenFields = this.loadShowHiddenFieldsState();
        return;
      }

      const selectedFormat = this.formatStore.selectedFormat();
      if (selectedFormat?.protected) {
        this.closeDialogWithError();
        return;
      }

      this.showHiddenFields = false;
    });
  }

  public ngOnInit(): void {
    this.loadFormat();
    this.specStore.loadSpecContract(this.specStore.selectedSpecId);
  }

  public ngOnDestroy(): void {
    this.formatStore.updateSelectedFormatId(null);
    this.specStore.updateSelectedSpecId(null);
  }

  protected getIconConfiguration(icon: string): IconConfiguration {
    return IconConfiguration.xLarge(icon, '', 32);
  }

  protected remainChars(length: number, limit: number): number {
    return limit - length;
  }

  protected closeDialogWithError(): void {
    this.notificationService.error('FORMAT_LIST.SNACKBAR.PROTECTED');
    this.closeDialog();
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected async onSubmit(): Promise<void> {
    const formatObject = this.formatFormService.generateFormatObject();

    if (!formatObject) {
      return;
    }

    const canProceed = await this.formatParametersChanged();
    if (!canProceed) {
      return;
    }

    this.updateOrCreateFormat(formatObject, !!this.formatStore.selectedFormatId()).subscribe(format => {
      if (format) {
        this.dialogRef.close(format);
      }
    });
  }

  protected sortSpecifications(specifications: Specification[]): Specification[] {
    return specifications.sort((firstSpec, secondSpec) => firstSpec.key.localeCompare(secondSpec.key));
  }

  protected onShowHiddenFieldsChange(): void {
    this.showHiddenFields = !this.showHiddenFields;
    try {
      localStorage.setItem(this.showHiddenFieldsStorageKey, String(this.showHiddenFields));
    } catch {
      this.showHiddenFields = false;
    }
  }

  private async formatParametersChanged(): Promise<boolean> {
    if (this.formatFormService.formatParametersChanged() && !this.formatFormService.warningDialogDisabled) {
      return firstValueFrom(this.matDialog.open(ChangeWarningComponent).afterClosed()).then((result: ChangeWarningResult) => {
        if (!result || result?.action === 'cancel') {
          return false;
        }

        result.dontShowAgain && this.formatFormService.disableDialogWarning();
        return true;
      });
    }

    return Promise.resolve(true);
  }

  private updateOrCreateFormat(format: TCreate & { key?: string }, isEdit: boolean): Observable<TFormat> {
    if (isEdit) {
      delete format.key;

      return this.updateFormat(format as TUpdate);
    }

    return this.createFormat(format as TCreate);
  }

  private createFormat(format: TCreate): Observable<TFormat> {
    return this.formatService.createFormat(format);
  }

  private updateFormat(format: TUpdate): Observable<TFormat> {
    return this.formatService.updateFormat(this.formatStore.selectedFormatId(), format);
  }

  private loadShowHiddenFieldsState(): boolean {
    try {
      const stored = localStorage.getItem(this.showHiddenFieldsStorageKey);
      return stored === 'true';
    } catch {
      return false;
    }
  }

  private loadFormat(): void {
    const formatId = this.data.id;
    if (!formatId) {
      return;
    }

    this.formatStore.loadFormatById(formatId).subscribe({
      next: () => {
        this.formatStore.updateSelectedFormatId(formatId);
        this.specControl.setValue(this.formatStore.selectedFormat()?.specificationReference?.id ?? null);
      },
      error: error => {
        console.error(`${this.logPrefix}: error on loading format by id`, error.error);
        this.notificationService.error(error.error.errorMessage);
        this.closeDialog();
      }
    });
  }
}
